import { ref, reactive, computed } from 'vue';

import { defineStore } from 'pinia';

import { jwtDecode } from 'jwt-decode';

import { useCacheRequisicoesStore } from '@/stores/cacheRequisicoes';

export const useAuthStore = defineStore('authstore', () => {
    const usuario = reactive({
        dados: {
            id: null,
            nome: '',
            nome_usual: '',
            email: '',
            cpf: '',
            avatar: '',
        },

        relacoes: {
            pessoa_fisica_id: null,
            funcionario_id: null,
        },

        permissoes: {
            tem_coordenacao: false,
            tem_pagamentos: false,
            tem_documentos_assinar: false,
            tem_documentos_assinados: false,
            tem_contratos: false,
        },

        status: {
            funcionario: false,
        },

        setor: {
            chefia: false,
            nome: '',
            setor_id: null,
            setores_chefiados_ids: [],
        }
    });

    const token = ref(null);
    const refreshToken = ref(null);
    const isAuthenticated = computed(() => !!token.value);

    const setUsuario = (newUser) => {        
        usuario.dados.id = newUser.id;
        usuario.dados.nome = newUser.nome;
        usuario.dados.nome_usual = newUser.nome_usual;
        usuario.dados.email = newUser.email;
        usuario.dados.cpf = newUser.cpf;
        usuario.dados.avatar = newUser.avatar;

        usuario.relacoes.pessoa_fisica_id = newUser.pessoa_fisica_id;
        usuario.relacoes.funcionario_id = newUser.funcionario_id;

        usuario.status.funcionario = newUser.funcionario;

        usuario.permissoes.tem_coordenacao = newUser.tem_coordenacao;
        usuario.permissoes.tem_pagamentos = null;
        usuario.permissoes.tem_documentos_assinar = null;
        usuario.permissoes.tem_documentos_assinados = null;
        usuario.permissoes.tem_contratos = null;

        usuario.setor.chefia = newUser.chefia;
        usuario.setor.nome = newUser.setor_nome;
        usuario.setor.setor_id = newUser.setor_id;
        usuario.setor.setores_chefiados_ids = newUser.setores_chefiados_ids ?? [];
    };

    const setTemDocumentosAssinar = (tem_documentos_assinar) => {
        usuario.permissoes.tem_documentos_assinar = tem_documentos_assinar;
    }
    const getTemDocumentosAssinar = () => {
        return usuario.permissoes.tem_documentos_assinar;
    }
    const setTemDocumentosAssinados = (tem_documentos_assinados) => {
        usuario.permissoes.tem_documentos_assinados = tem_documentos_assinados;
    }
    const getTemDocumentosAssinados = () => {
        return usuario.permissoes.tem_documentos_assinados;
    }
    const setTemPagamentos = (tem_pagamentos) => {
        usuario.permissoes.tem_pagamentos = tem_pagamentos;
    }
    const getTemPagamentos = () => {
        return usuario.permissoes.tem_pagamentos;
    }
    const setTemContratos = (tem_contratos) => {
        usuario.permissoes.tem_contratos = tem_contratos;
    }
    const getTemContratos = () => {
        return usuario.permissoes.tem_contratos;
    }
    const setTemCoordenacao = (tem_coordenacao) => {
        usuario.permissoes.tem_coordenacao = tem_coordenacao;
    }
    const getTemCoordenacao = () => {
        return usuario.permissoes.tem_coordenacao;
    }
    const getFuncionario = () => {
        return usuario.status.funcionario;
    }
    const setFuncionario = (funcionario) => {
        usuario.status.funcionario = funcionario;
    }
    const getFuncionarioId = () => {
        return usuario.relacoes.funcionario_id;
    }
    const setFuncionarioId = (funcionario_id) => {
        usuario.relacoes.funcionario_id = funcionario_id;
    }
    const getChefia = () => {
        return usuario.setor.chefia;
    }
    const setChefia = (chefia) => {
        usuario.setor.chefia = chefia;
    }
    const getSetoresChefiadosIds = () => {
        return usuario.setor.setores_chefiados_ids ?? [];
    }
    const setSetoresChefiadosIds = (setores_chefiados_ids) => {
        usuario.setor.setores_chefiados_ids = setores_chefiados_ids ?? [];
    }
    const getToken = () => {
        return token.value
    };
    const setToken = (newToken) => {
        token.value = newToken;
    };
    const getRefreshToken = () => {
        return refreshToken.value
    };
    const setRefreshToken = (newRefreshToken) => {
        refreshToken.value = newRefreshToken;
    };
    const getDecodedToken = () => {
        if (!token.value) {
            return null;
        }
        return jwtDecode(token.value);
    }
    // As três sempre devolvem array: quem chama usa `.includes`/`.some` direto, em
    // `v-if` e `computed`. Uma claim ausente no token (usuário sem grupo, por
    // exemplo) viraria `undefined` e derrubaria o render, não só esconderia o item.
    const getUsuarioPermissoes = () => {
        const decodedToken = getDecodedToken();
        return decodedToken?.permissoes ?? [];
    }
    const getUsuarioGrupos = () => {
        const decodedToken = getDecodedToken();
        return decodedToken?.grupos ?? [];
    }
    const getUsuarioPseudonimos = () => {
        const decodedToken = getDecodedToken();
        return decodedToken?.pseudonimos ?? [];
    }

    const logout = () => {
        usuario.dados.id = null;
        usuario.dados.nome = '';
        usuario.dados.nome_usual = '';
        usuario.dados.email = '';
        usuario.dados.cpf = '';
        usuario.dados.avatar = '';

        usuario.relacoes.pessoa_fisica_id = null;
        usuario.relacoes.funcionario_id = null;
        
        usuario.permissoes.tem_coordenacao = false;
        usuario.permissoes.tem_pagamentos = false;
        usuario.permissoes.tem_documentos_assinar = false;
        usuario.permissoes.tem_documentos_assinados = false;
        usuario.permissoes.tem_contratos = false;
        
        usuario.status.funcionario = false;
        
        usuario.setor.chefia = false;
        usuario.setor.nome = '';
        usuario.setor.setor_id = null;
        usuario.setor.setores_chefiados_ids = [];

        token.value = null;
        refreshToken.value = null;

        // O cache de requisições guarda dados do usuário que está saindo
        useCacheRequisicoesStore().limpar();

        // This will trigger the storage event in other tabs
        localStorage.setItem('logout-event', Date.now().toString());
    };

    return {
        token,
        refreshToken,
        usuario,
        isAuthenticated,

        setUsuario,

        setToken,
        getToken,

        getDecodedToken,
        getUsuarioPermissoes,
        getUsuarioGrupos,
        getUsuarioPseudonimos,

        getRefreshToken,
        setRefreshToken,

        setTemCoordenacao,
        getTemCoordenacao,

        setFuncionario,
        getFuncionario,

        getFuncionarioId,
        setFuncionarioId,

        setTemPagamentos,
        getTemPagamentos,

        setTemContratos,
        getTemContratos,

        setTemDocumentosAssinar,
        getTemDocumentosAssinar,

        setTemDocumentosAssinados,
        getTemDocumentosAssinados,

        getChefia,
        setChefia,

        getSetoresChefiadosIds,
        setSetoresChefiadosIds,

        logout,
    }
}, { persist: true });